import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { AlertTemplate } from "@/api/types";
import { useCreateTemplate, useUpdateTemplate } from "@/api/templates";
import { ApiError } from "@/api/client";

const EMPTY_PAYLOAD = JSON.stringify(
  {
    external_id: "{{ external_id }}",
    name: "noise:{{ scenario_slug }}",
    severity: "warning",
    labels: { scenario: "{{ scenario_slug }}" },
  },
  null,
  2,
);

export function TemplateEditDialog({
  open,
  onOpenChange,
  existing,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  existing: AlertTemplate | null;
}) {
  const isCreate = !existing;
  const create = useCreateTemplate();
  const update = useUpdateTemplate();
  const [id, setId] = useState(existing?.id ?? "");
  const [name, setName] = useState(existing?.name ?? "");
  const [description, setDescription] = useState(existing?.description ?? "");
  const [rate, setRate] = useState<string>(String(existing?.default_rate_per_minute ?? 6));
  const [payloadText, setPayloadText] = useState(
    JSON.stringify(existing?.payload_template ?? JSON.parse(EMPTY_PAYLOAD), null, 2),
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setId(existing?.id ?? "");
    setName(existing?.name ?? "");
    setDescription(existing?.description ?? "");
    setRate(String(existing?.default_rate_per_minute ?? 6));
    setPayloadText(
      JSON.stringify(existing?.payload_template ?? JSON.parse(EMPTY_PAYLOAD), null, 2),
    );
    setError(null);
  }, [existing, open]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    let payload_template: Record<string, unknown>;
    try {
      payload_template = JSON.parse(payloadText || "{}");
    } catch {
      setError("Payload template must be valid JSON.");
      return;
    }
    const body: AlertTemplate = {
      id,
      name,
      description,
      payload_template,
      default_rate_per_minute: Number(rate) || 6,
      owner: existing?.owner ?? null,
    };
    try {
      if (isCreate) await create.mutateAsync(body);
      else await update.mutateAsync(body);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{isCreate ? "New template" : `Edit ${existing!.id}`}</DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={onSubmit}>
          <div className="space-y-2">
            <Label>ID</Label>
            <Input value={id} onChange={(e) => setId(e.target.value)} disabled={!isCreate} required />
          </div>
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Default rate (per minute)</Label>
            <Input type="number" value={rate} onChange={(e) => setRate(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Payload template (JSON, Jinja2)</Label>
            <Textarea
              value={payloadText}
              onChange={(e) => setPayloadText(e.target.value)}
              rows={10}
              className="font-mono text-xs"
            />
          </div>
          {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
          <DialogFooter>
            <Button type="submit" disabled={create.isPending || update.isPending}>
              {isCreate ? "Create" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
